// Строчные узлы-ссылки: сноска (4.10.9), ссылка на источник [N] (4.14), перекрёстная ссылка на объект; метка auto -
// текст, дописанный сайтом при загрузке .docx.
// Номера/тексты показывают отображения (node views) по сервису нумерации - в документе хранятся только id.
import { Node, Mark, mergeAttributes } from '@tiptap/core';
import type { JSONContent } from '@tiptap/core';
import { idAttr, jsonAttr, registryNodeView } from './util';

/** Сноска: content - строчное содержимое (ProseMirror JSON: text с метками, mathInline). */
export const Footnote = Node.create({
  name: 'footnote',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  addAttributes() {
    return { ...idAttr(), content: jsonAttr<JSONContent[]>('content', []) };
  },
  parseHTML() { return [{ tag: 'sup[data-type="footnote"]' }]; },
  renderHTML({ HTMLAttributes }) {
    return ['sup', mergeAttributes(HTMLAttributes, { 'data-type': 'footnote', class: 'sto-fn' }), '*'];
  },
  renderText() { return ''; },
  addNodeView: registryNodeView('footnote'),
});

export interface CiteItem { source: string; loc?: string; auto?: boolean }

/** Ссылка на источник: items = [{source: id записи библиотеки, loc: 'с. 45'}] -> «[3, с. 45]», «[1–4]».
 *  items[].auto - запись в ссылку дописал импорт .docx: на источник не было ссылки в тексте
 *  (stoweb/autorefs.ensure_cites); ссылка выделяется, пока автор её не примет (флаг - в items, а не отдельным
 *  атрибутом узла: документ с ним открывается и прежней сборкой интерфейса). */
export const Cite = Node.create({
  name: 'cite',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  addAttributes() { return { items: jsonAttr<CiteItem[]>('items', []) }; },
  parseHTML() { return [{ tag: 'span[data-type="cite"]' }]; },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { 'data-type': 'cite', class: 'sto-cite' }), '[?]'];
  },
  renderText() { return '[?]'; },
  addNodeView: registryNodeView('cite'),
});

/** Перекрёстная ссылка: target - id рисунка/таблицы/формулы/листинга/приложения/заголовка; показывает живой номер.
 *  auto - ссылку дописал импорт .docx (в исходнике её не было, stoweb/autorefs.py): выделяется, пока автор её не примет. */
export const Xref = Node.create({
  name: 'xref',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      target: { default: '', parseHTML: (el) => el.getAttribute('data-target') || '', renderHTML: (a) => ({ 'data-target': a.target }) },
      auto: { default: false, parseHTML: (el) => el.getAttribute('data-auto') === '1', renderHTML: (a) => (a.auto ? { 'data-auto': '1' } : {}) },
    };
  },
  parseHTML() { return [{ tag: 'span[data-type="xref"]' }]; },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { 'data-type': 'xref', class: 'sto-xref' }), '?'];
  },
  addNodeView: registryNodeView('xref'),
});

/** Метка «добавлено автоматически»: текст, который дописал сайт при загрузке .docx (ссылка «(рисунок 3)», абзац
 *  «Результат представлен на рисунке 3.», название объекта - stoweb/autorefs.py, autonames.py). Редактор подчёркивает
 *  его пунктиром с подсказкой, живая проверка (sto_intel/lint.ts, AUTO) предлагает «Принять» - метка снимается.
 *  В DOCX метки нет (convert/inline.py её не выводит). Набранное сразу за меткой её не продолжает (inclusive: false). */
export const AUTO_TIP = 'Добавлено автоматически при загрузке файла - проверьте';
export const AutoMark = Mark.create({
  name: 'auto',
  inclusive: false,
  parseHTML() { return [{ tag: 'span[data-auto-text]' }]; },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { 'data-auto-text': '1', class: 'sto-auto', title: AUTO_TIP }), 0];
  },
});

export const inlineExtensions = () => [Footnote, Cite, Xref, AutoMark];
